import AuthService from '@/services/AuthService';
import axios from 'axios';

const getInitialState = function () {
  var state = {
    status: {
      loggedIn: false,
    },
    token: null,
    user: null,
    impersonator: null,
    impersonating: false,
    userWorkInfo: null,
    userResearchCreds: null,
    nextURL: sessionStorage.getItem('nextURL') || '/networks',
    managedNetworks: null,
    countries: null,
    states: null,
  };

  const user = JSON.parse(localStorage.getItem('user') || null);
  const token = localStorage.getItem('token') || 'false';
  const impersonator = JSON.parse(localStorage.getItem('impersonator') || null);
  const impersonating = localStorage.getItem('impersonating') === 'true';

  if (user) {
    state.status.loggedIn = true;
    state.token = token;
    state.user = user;
    state.impersonator = impersonator;
    state.impersonating = impersonating;
  }

  return state;
};

let service = new AuthService();

export default {
  namespaced: false,
  state: getInitialState(),

  actions: {
    async signIn({ commit, dispatch }, authCode) {
      //await axios.get('/sanctum/csrf-cookie');

      return service.login({ code: authCode }).then(
        data => {
          commit('token', data.token.token);
          commit('resetUserInfo', data.user);
          dispatch('loginOrLogoutDone');
          return Promise.resolve(data.user);
        },
        error => {
          commit('loginFailure');
          return Promise.reject(error);
        }
      );
    },
    // login as another user with a form submission (username/email)
    loginAs({ dispatch, commit }, form) {
      return service.loginAs(form).then(data => {
        commit('token', data.token.token);
        commit('resetUserInfo', data.user);

        dispatch('loginOrLogoutDone');
        return Promise.resolve(data.user);
      });
    },
    logout({ commit, dispatch }) {
      // ensure the user is logged out before we call the API.
      commit('logout');
      return service.logout().then(() => {
        dispatch('loginOrLogoutDone');
        dispatch('clearPersistedStates');
      });
    },

    // called when there is a logout or login event of any kind
    // resets everything and helps the interface "refresh"
    loginOrLogoutDone({ dispatch }) {
      dispatch('network/resetNetworks');
      dispatch('network/resetTrendingNetworks');
    },

    /**
     * This function will make a request to the server to get the user's information, and then it will
     * commit the user's information to the store.
     * Best use of this function is if your component modifies the currently logged in user's profile information.
     * @param context - The context object is the same as the store object.
     */
    reloadUserInfo(context) {
      axios.get('auth/me').then(resp => {
        context.commit('resetUserInfo', resp.data);
      });
    },

    resetUserInfo(context, user) {
      context.commit('resetUserInfo', user);
    },

    resetUserWorkInfo(context, userWorkInfo) {
      context.commit('resetUserWorkInfo', userWorkInfo);
    },

    resetUserResearchCreds(context, userResearchCreds) {
      context.commit('resetUserResearchCreds', userResearchCreds);
    },

    setResettingToken(context) {
      context.commit('setResettingToken');
    },

    // the old token is invalid, so let's refresh the token
    getNewToken(context) {
      return service.refreshToken().then(token => {
        console.log('Update refreshed token to ', token);
        context.commit('token', token);
        return token;
      });
    },

    setNextURL(context, url) {
      context.commit('setNextURL', url);
    },

    // always loads from the networks (no cache)
    loadManagedNetworks(context) {
      return new Promise((resolve, reject) => {
        axios
          .get('my-managed-networks')
          .then(resp => {
            context.commit('setManagedNetworks', resp.data);
            resolve(resp.data);
          })
          .catch(error => reject(error));
      });
    },

    loadCountries(context) {
      return new Promise((resolve, reject) => {
        if (!context.state.countries) {
          axios
            .get('rpc-countries')
            .then(resp => {
              context.commit('setCountries', resp.data);
              resolve(resp.data);
            })
            .catch(error => reject(error));
        } else {
          resolve(context.state.countries);
        }
      });
    },

    loadStates(context) {
      return new Promise((resolve, reject) => {
        if (!context.state.states) {
          axios
            .get('us-states')
            .then(resp => {
              context.commit('setStates', resp.data);
              resolve(resp.data);
            })
            .catch(error => reject(error));
        } else {
          resolve(context.state.states);
        }
      });
    },

    fetchUserWorkInfo({ commit }) {
      return axios
        .get('/profile/workinfo')
        .then(response => {
          const userWorkInfo = response.data.model;
          commit('setUserWorkInfo', userWorkInfo);
        })
        .catch(error => {
          console.error(error);
          throw error;
        });
    },

    fetchUserResearchCreds({ commit }) {
      return axios
        .get('/profile/researchcreds')
        .then(response => {
          const userResearchCreds = response.data.model;
          commit('setUserResearchCreds', userResearchCreds);
        })
        .catch(error => {
          console.error(error);
          throw error;
        });
    },

    impersonate({ dispatch, commit }, userIdOrName) {
      let userId = userIdOrName;

      if (isNaN(userId)) {
        return axios.get('users?query=' + userIdOrName + '&limit=1').then((resp) => {
          if (resp.data.length > 0) {
            userId = resp.data[0].id;
            return axios.post(`impersonate/${userId}`).then((response) => {
              // store the new token
              commit('token', response.data.token.token);
              // store the user that we're impersonating
              commit('resetUserInfo', response.data.user);
              // store the impersonator (original user)
              commit('setImpersonator', response.data.impersonator);
              // set the impersonating flag
              commit('setImpersonating', true);
              dispatch('loginOrLogoutDone');
              return Promise.resolve();
            });
          } else {
            return Promise.reject(new Error('User not found'));
          }
        });
      } else {
        return axios.post(`impersonate/${userId}`).then((response) => {
          // store the new token
          commit('token', response.data.token.token);
          // store the user that we're impersonating
          commit('resetUserInfo', response.data.user);
          // store the impersonator (original user)
          commit('setImpersonator', response.data.impersonator);
          // set the impersonating flag
          commit('setImpersonating', true);
          dispatch('loginOrLogoutDone');
          return Promise.resolve();
        });
      }
    },

    exitImpersonate({ dispatch, commit, state }) {
      if (!state.impersonator) {
        console.error('No impersonator found in state');
        return Promise.reject(new Error('Not currently impersonating'));
      }
      console.log('Exiting impersonation with impersonator ID:', state.impersonator.user_id);
      return axios.post('impersonate-exit', {
        impersonator_id: state.impersonator.id || state.impersonator.user_id
      }).then((response) => {
        // store the new token
        commit('token', response.data.token.token);
        // restore the original user
        commit('resetUserInfo', response.data.user);
        // clear the impersonator
        commit('clearImpersonator');
        // set the impersonating flag
        commit('setImpersonating', false);
        dispatch('loginOrLogoutDone');
        return Promise.resolve();
      }).catch(error => {
        console.error('Error exiting impersonation:', error);
        // even if there's an error, try to reset the state
        commit('clearImpersonator');
        commit('setImpersonating', false);
        return Promise.reject(error);
      });
    },
  },
  getters: {
    // returns true if a user is logged in
    auth: state => {
      return state.status.loggedIn;
    },
    user: state => {
      return state.user;
    },
    userWorkInfo: state => {
      return state.userWorkInfo;
    },
    userResearchCreds: state => {
      return state.userResearchCreds;
    },
    zoomUser: state => {
      return state.user.zoom_user;
    },
    isUserIntegratedToZoom: state => {
      var linked = false;
      if (state.user.zoom_user && state.user.zoom_user.zoom_user_id) linked = true;

      return linked;
    },
    token: state => {
      return state.token;
    },
    getNextURL: state => {
      return state.nextURL;
    },
    managedNetworks: state => {
      return state.managedNetworks;
    },
    countries: state => {
      return state.countries;
    },
    states: state => {
      return state.states;
    },
    isImpersonating: state => {
      return state.impersonating;
    },
    impersonator: state => {
      return state.impersonator;
    },
  },
  mutations: {
    token(state, newToken) {
      localStorage.setItem('token', newToken);

      state.token = newToken;
      state.status.loggedIn = newToken != null;

      axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    },

    loginFailure(state) {
      state.status.loggedIn = false;
      state.user = null;
    },
    logout(state) {
      state.status.loggedIn = false;
      state.user = null;
      state.impersonator = null;
      state.impersonating = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('impersonator');
      localStorage.removeItem('impersonating');

      // remove the header
      delete axios.defaults.headers.common['Authorization'];
    },
    resetUserInfo(state, user) {
      state.user = user;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    resetUserWorkInfo(state, userWorkInfo) {
      state.userWorkInfo = userWorkInfo;
      localStorage.setItem('userWorkInfo', JSON.stringify(state.userWorkInfo));
    },
    resetUserResearchCreds(state, userResearchCreds) {
      state.userResearchCreds = userResearchCreds;
      localStorage.setItem('userResearchCreds', JSON.stringify(state.userResearchCreds));
    },

    setImpersonator(state, impersonator) {
      state.impersonator = impersonator;
      localStorage.setItem('impersonator', JSON.stringify(impersonator));
    },

    clearImpersonator(state) {
      state.impersonator = null;
      localStorage.removeItem('impersonator');
    },

    setNextURL(state, url) {
      sessionStorage.setItem('nextURL', url);
      state.nextURL = url;
    },

    setManagedNetworks(state, networks) {
      state.managedNetworks = networks;
    },

    setCountries(state, countries) {
      state.countries = countries;
    },

    setStates(state, states) {
      state.states = states;
    },

    setUserWorkInfo(state, userWorkInfo) {
      state.userWorkInfo = userWorkInfo;
    },

    setUserResearchCreds(state, userResearchCreds) {
      state.userResearchCreds = userResearchCreds;
    },

    REMOVE_NETWORK(state, index) {
      state.managedNetworks.splice(index, 1);
    },

    RESET_STATE(state) {
      Object.assign(state, getInitialState());
    },

    setImpersonating(state, status) {
      state.impersonating = status;
      localStorage.setItem('impersonating', status);
    },
  },
};
